import type { Reference, StoreObject } from '@apollo/client'

import { graphql } from '~publish/gql'
import type { PostsEdge } from '~publish/gql/graphql'
import { isOfType } from '~publish/helpers/typeGuards'

import { useTypedMutation } from '../../hooks/useTypedMutation'

export const DeletePost = graphql(/* GraphQL */ `
  mutation DeletePost($id: PostId!) {
    deletePostV2(input: { id: $id }) {
      __typename
      ... on MutationError {
        message
      }
      ... on DeletePostSuccess {
        id
      }
    }
  }
`)

export const useDeletePost = () =>
  useTypedMutation(DeletePost, (data) => data.deletePostV2, {
    successTypename: 'DeletePostSuccess',
    optimisticResponse: (variables) => ({
      deletePostV2: {
        __typename: 'DeletePostSuccess' as const,
        id: variables.id,
      },
    }),
    update: (cache, { data }, { variables }) => {
      const result = data?.deletePostV2
      if (!isOfType(result, 'DeletePostSuccess')) {
        return
      }

      const deletedPostId = variables?.id
      if (!deletedPostId) {
        return
      }

      // Remove post from all posts lists
      cache.modify({
        fields: {
          posts(
            existingPostsConnection: Reference | StoreObject,
            { readField },
          ) {
            if (existingPostsConnection && '__ref' in existingPostsConnection) {
              return existingPostsConnection
            }

            const edges =
              readField<PostsEdge[]>('edges', existingPostsConnection) || []

            const filteredEdges = edges.filter(
              (edge: PostsEdge) => readField('id', edge.node) !== deletedPostId,
            )

            return {
              ...existingPostsConnection,
              edges: filteredEdges,
            }
          },
        },
      })

      cache.gc()
    },
  })
